import { ApiProject, ApiSimpleComponent } from "@incendium/api";
import { Stack } from "@mui/material";
import SavingButton from "Components/UI/SavingButton";
import { StyledDrawerActions } from "Components/UI/StyledDrawer";
import { saveComponent } from "features/campaigns";
import { AccordianChartBuilderSidebarBlock } from "features/chartLibrary";
import { useLightningLanderContext } from "features/lightningLanders/providers/LightningLanderProvider";
import { CodeEditor } from "features/uiBuilder";
import { useNotification } from "Hooks";
import { useCallback, useState } from "react";
import { TextValidator, ValidatorForm } from "react-material-ui-form-validator";

interface ILightningLandersHeadProps {
  project: ApiProject;
  saveToken: string | null;
}

function LightningLandersHead({
  project,
  saveToken,
}: ILightningLandersHeadProps) {
  const { component, setComponent } = useLightningLanderContext();
  const [saving, setSaving] = useState(false);
  const [htmlValue, setHtmlValue] = useState(component.head || "");
  const [favicon, setFavicon] = useState(component.favicon || "");
  const [seoTitle, setSeoTitle] = useState(component.seoTitle || "");
  const [seoDescrition, setSeoDescrition] = useState(
    component.seoDescrition || ""
  );
  const { showErrorNotification, showSuccessNotification } = useNotification();

  // const onClose = useCallback(() => {
  //   setOpen(false);
  // }, [setOpen]);

  const onChange = useCallback((v) => {
    setHtmlValue(v);
  }, []);

  const patchComponent = useCallback(async () => {
    setSaving(true);

    try {
      const res = await saveComponent(
        project.id as number,
        {
          ...component,
          head: htmlValue,
          favicon,
          seoTitle,
          seoDescrition,
        },
        saveToken ? saveToken : undefined
      );

      setComponent(res.component as ApiSimpleComponent);
      showSuccessNotification(`${component.name} Head Saved`);
    } catch (error) {
      showErrorNotification(
        `Internal Error connecting subdomain, please try again`
      );
    }

    setSaving(false);
    // onClose();
  }, [
    project,
    component,
    saveToken,
    htmlValue,
    favicon,
    seoTitle,
    seoDescrition,
    showSuccessNotification,
    showErrorNotification,
    setComponent,
  ]);

  return (
    <ValidatorForm onSubmit={patchComponent}>
      <AccordianChartBuilderSidebarBlock
        title="General Settings"
        subTitle="Set SEO and favicon settings for your html page."
      >
        <Stack mt={3} spacing={3}>
          <TextValidator
            variant="outlined"
            size="small"
            fullWidth
            label="Favicon Link"
            helperText="This is a small icon displayed in the browser's address bar or tab, representing a website for easy identification"
            name={"favicon"}
            value={favicon}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setFavicon(e.target.value)
            }
          />
          <TextValidator
            variant="outlined"
            size="small"
            fullWidth
            label="SEO title"
            helperText="Craft a compelling SEO title to enhance your website's search engine visibility."
            name={"seoTitle"}
            value={seoTitle}
            validators={["maxStringLength:60"]}
            errorMessages={[
              "Please ensure your SEO title does not exceed 60 characters.",
            ]}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setSeoTitle(e.target.value)
            }
          />
          <TextValidator
            variant="outlined"
            size="small"
            fullWidth
            label="SEO description"
            helperText="Compose a concise SEO description to provide search engines with relevant information about your website."
            name={"seoDescrition"}
            value={seoDescrition}
            validators={["maxStringLength:160"]}
            errorMessages={[
              "Please ensure your SEO description does not exceed 160 characters.",
            ]}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setSeoDescrition(e.target.value)
            }
          />
        </Stack>
      </AccordianChartBuilderSidebarBlock>
      <AccordianChartBuilderSidebarBlock
        title="Custom Head HTML"
        subTitle="Add custom code to be added to your pages head section."
      >
        <CodeEditor
          mode="html"
          title={"Custom head"}
          onChange={onChange}
          value={htmlValue}
          height={400}
        />
      </AccordianChartBuilderSidebarBlock>
      <StyledDrawerActions>
        <SavingButton
          variant="contained"
          color="primary"
          saving={saving}
          type="submit"
        >
          Save
        </SavingButton>
      </StyledDrawerActions>
    </ValidatorForm>
  );
}

export default LightningLandersHead;
